import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { AddIcon } from '../../../../../../design/static/icons/AddIcon';
import { EnableFor } from '../../../../../containers/EnableForRole';
import { useLoader } from '../../../../../helpers/useLoader';
import { ImageScopeName } from '../../../../annotation/question.model';
import { useProjectDetailsPermissions } from '../../../projectDetails.permissions';
import { IProjectDetailsToolsBl } from '../projectDetailsTools.bl';
import { IQuestion } from '../projectDetailsTools.models';
import { QuestionModalContainer } from './QuestionModal.container';

export const ProjectAttributeFormContainer = observer((): ReactElement => {
  const projectDetailsToolsBl = useInjection(IProjectDetailsToolsBl.$);
  const permissions = useProjectDetailsPermissions();

  const { t } = useTranslator();
  const { isLoading, withLoaderAsync } = useLoader();
  const [showModal, setShowModal] = useState(false);

  const handleSubmit = async (questionModel: IQuestion): Promise<void> => {
    await withLoaderAsync(async () => {
      await projectDetailsToolsBl.addQuestionAsync(questionModel);
      setShowModal(false);
    }, 'submit-question-form-button');
  };

  const handleModalToggle = (): void => {
    setShowModal((prev) => !prev);
  };

  const questions = projectDetailsToolsBl.store.questions;

  return (
    <>
      <EnableFor
        hasPermission={permissions.canAddQuestion()}
        hideWhenNoAccess
      >
        <Button
          size='sm'
          variant='outlined'
          onClick={handleModalToggle}
          startDecorator={<AddIcon />}
        >
          {t('add')}
        </Button>
      </EnableFor>
      <QuestionModalContainer
        question={undefined}
        questionsCount={questions.length}
        selectedQuestionTypes={questions.filter((q) => q.scopes.includes(ImageScopeName)).map((q) => q.type)}
        existingQuestionsAndAttributes={questions.map((q) => ({ text: q.questionText, type: q.type }))}
        onSubmit={handleSubmit}
        onCancel={handleModalToggle}
        isReadonly={false}
        isAttribute={true}
        isLoading={isLoading}
        showModal={showModal}
        modalHeader={t('add_new_attribute')}
      />
    </>
  );
});
